import {
  ArrowRight,
  CheckCircle2,
  CircleAlert,
  Image as ImageIcon,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  attemptTone,
  isStaticMotionFallback,
  providerDisplay,
} from "./i2vOutcome";
import { i2vAttemptsAnchorId } from "./sceneFlags";
import type { SceneI2VAudit, SceneI2VProviderAttempt } from "./scriptStory";

type I2VFallbackChainProps = {
  sceneI2VByScene: Map<string | number, SceneI2VAudit>;
  loading?: boolean;
};

function attemptsForAudit(audit: SceneI2VAudit): SceneI2VProviderAttempt[] {
  if (audit.attempts?.length) return audit.attempts;
  if (!audit.raiFiltered && !audit.fallbackReason) return [];
  return [
    {
      provider: audit.fallbackFrom ?? audit.provider,
      lifecycleStatus: "failed",
      errorCode: audit.raiFiltered ? "rai_filtered" : audit.fallbackReason,
      errorMessage: audit.raiReason ?? audit.fallbackReason,
      displayMessage: audit.raiReason ?? audit.fallbackReason,
    },
  ];
}

function uniqueSceneAudits(
  byScene: Map<string | number, SceneI2VAudit>,
): Array<{ sceneId: string | number; audit: SceneI2VAudit }> {
  const seen = new Set<SceneI2VAudit>();
  const result: Array<{ sceneId: string | number; audit: SceneI2VAudit }> = [];
  for (const [sceneId, audit] of byScene) {
    if (seen.has(audit)) continue;
    seen.add(audit);
    result.push({ sceneId, audit });
  }
  return result.sort((left, right) =>
    String(left.sceneId).localeCompare(String(right.sceneId), undefined, {
      numeric: true,
    }),
  );
}

function errorText(attempt: SceneI2VProviderAttempt) {
  if (attempt.displayMessage) return attempt.displayMessage;
  if (attempt.errorCategory === "minor") {
    return "Veo rejected the input because it may depict a minor.";
  }
  if (attempt.errorCategory === "celebrity") {
    return "Veo rejected the input because it may depict a celebrity or prominent person.";
  }
  return attempt.errorMessage ?? attempt.errorCode ?? null;
}

const AttemptCard = ({ attempt }: { attempt: SceneI2VProviderAttempt }) => {
  const tone = attemptTone(attempt);
  const Icon =
    tone === "success"
      ? CheckCircle2
      : tone === "failure"
        ? XCircle
        : CircleAlert;
  const classes =
    tone === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-950"
      : tone === "failure"
        ? "border-red-200 bg-red-50 text-red-950"
        : "border-amber-200 bg-amber-50 text-amber-950";
  const iconClass =
    tone === "success"
      ? "text-emerald-600"
      : tone === "failure"
        ? "text-red-600"
        : "text-amber-600";
  const message = errorText(attempt);
  const provider = providerDisplay(attempt);

  return (
    <div className={`min-w-56 rounded-lg border p-3 ${classes}`}>
      <div className="flex items-start gap-2">
        <Icon className={`mt-0.5 size-4 shrink-0 ${iconClass}`} />
        <div className="min-w-0">
          <div className="font-medium">{provider.name}</div>
          {provider.modelId ? (
            <div className="break-all font-mono text-[11px] opacity-75">
              {provider.modelId}
              {provider.modelVersion &&
              !provider.modelId.includes(provider.modelVersion)
                ? ` · version ${provider.modelVersion}`
                : null}
            </div>
          ) : null}
          {provider.details ? (
            <div className="text-[11px] opacity-75">{provider.details}</div>
          ) : null}
          <div className="text-xs opacity-75">
            {attempt.lifecycleStatus?.replaceAll("_", " ") ?? "attempted"}
          </div>
          {message ? <p className="mt-2 text-xs leading-5">{message}</p> : null}
          {attempt.supportCodes?.length ? (
            <p className="mt-1 text-[11px] opacity-70">
              Support code {attempt.supportCodes.join(", ")}
            </p>
          ) : null}
          {attempt.providerHistory?.length ? (
            <div className="mt-2 space-y-1 border-t border-current/15 pt-2">
              {attempt.providerHistory.map((item, index) => {
                const worked = item.outcome === "accepted";
                return (
                  <div
                    className="flex items-center gap-1.5 text-[11px]"
                    key={`${item.concreteProvider}-${index}`}
                  >
                    {worked ? (
                      <CheckCircle2 className="size-3 text-emerald-600" />
                    ) : (
                      <XCircle className="size-3 text-red-600" />
                    )}
                    <span>
                      {(item.concreteProvider ?? "provider").replaceAll(
                        "_",
                        " ",
                      )}
                      {" · "}
                      {item.outcome ?? item.errorCode ?? "attempted"}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export const I2VFallbackChain = ({
  sceneI2VByScene,
  loading = false,
}: I2VFallbackChainProps) => {
  const scenes = uniqueSceneAudits(sceneI2VByScene).filter(
    ({ audit }) =>
      attemptsForAudit(audit).length > 0 ||
      audit.fallbackApplied ||
      audit.finalSceneType === "StaticImageScene",
  );
  if (loading || scenes.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>AI video generation</CardTitle>
          <Badge variant="outline">Provider fallback chain</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Green steps worked. Red steps failed. Safety labels are provider
          classifications, not confirmed facts about the person in the photo.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {scenes.map(({ sceneId, audit }) => {
          const attempts = attemptsForAudit(audit);
          const staticFallback = isStaticMotionFallback(audit);
          const providerClipUsed = attempts.some(
            (attempt) => attemptTone(attempt) === "success",
          );
          return (
            <section
              className="space-y-2 scroll-mt-20"
              key={String(sceneId)}
              // The target of the staff scene-flag card's "i2v attempts" link (C2).
              id={i2vAttemptsAnchorId(sceneId)}
            >
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="font-medium">Scene {sceneId}</h4>
                {staticFallback ? (
                  <Badge variant="secondary">Photo motion fallback</Badge>
                ) : audit.fallbackApplied ? (
                  <Badge className="bg-emerald-600 text-white">
                    Generation recovered
                  </Badge>
                ) : providerClipUsed ? (
                  <Badge className="bg-emerald-600 text-white">
                    AI clip used
                  </Badge>
                ) : null}
              </div>
              <div className="flex items-stretch gap-2 overflow-x-auto pb-1">
                {attempts.map((attempt, index) => (
                  <div
                    className="flex shrink-0 items-center gap-2"
                    key={`${attempt.provider}-${attempt.attempt}-${index}`}
                  >
                    {index > 0 ? (
                      <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
                    ) : null}
                    <AttemptCard attempt={attempt} />
                  </div>
                ))}
                {staticFallback ? (
                  <div className="flex shrink-0 items-center gap-2">
                    {attempts.length ? (
                      <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
                    ) : null}
                    <div className="min-w-56 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-emerald-950">
                      <div className="flex items-start gap-2">
                        <ImageIcon className="mt-0.5 size-4 text-emerald-600" />
                        <div>
                          <div className="font-medium">
                            Photo motion fallback
                          </div>
                          <p className="mt-1 text-xs">
                            No provider-generated clip was selected. The
                            composer used the original photo with a local
                            pan/zoom effect.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </section>
          );
        })}
      </CardContent>
    </Card>
  );
};
